/**
 * EmbeddedOrderDetailView - Full order detail view for use within dialogs
 *
 * This is a copy of OrderDetail.tsx adapted for embedded use:
 * - Takes props instead of URL params
 * - Has a back button instead of navigation
 * - No QuickNavBar or breadcrumbs
 * - No navigation links that would leave the dialog
 */
import { Grid } from './Grid';
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Paper, Typography, Button, Chip, CircularProgress, Alert, Card, CardContent, Stack, IconButton, Tabs, Tab, LinearProgress, Stepper, Step, StepLabel, StepConnector, stepConnectorClasses, styled, Accordion, AccordionSummary, AccordionDetails, Tooltip, Avatar, CardHeader, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import {
  Person,
  LocalPharmacy,
  LocalShipping,
  Payment,
  Schedule as ScheduleIcon,
  CheckCircle,
  Error as ErrorIcon,
  Refresh,
  Assignment,
  Receipt,
  Science,
  Inventory,
  VerifiedUser,
  ExpandMore,
  ContentCopy,
  Code,
  Sms,
  NotificationsActive,
  History,
  AccessTime,
  Timer,
  ArrowBack,
} from '@mui/icons-material';
import { format } from 'date-fns';
import orderService from '../../services/orderService';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import { HardcopyViewer } from './HardcopyViewer';
import type { HardcopySignature } from './HardcopyViewer';
import { UserName } from './UserName';
import { OrderTimelineView } from '../orders/OrderTimelineView';
import { deriveOrderDrugList } from '../../utils/orderDrugList';
import { deriveOrderCompletionState } from '../../utils/orderCompletionState';
import { formatTimelineDescription } from '../../utils/formatters';
import { useCurrentPatientContext } from '../../hooks/useCurrentPatientContext';
import { useVerificationPhotos } from '../../hooks/useVerificationPhotos';
import { VerificationPhotoLightbox } from '../product-verification/VerificationPhotoLightbox';
import { PhoxEntrySourceChips, type PhoxEntrySource } from '../order-fulfillment/PhoxEntrySourceChips';
import type { VerificationPhoto } from '../../services/verificationPhotoService';

interface EmbeddedOrderDetailViewProps {
  orderId: string;
  pharmacyId: string;
  onBack: () => void;
}

interface StageHistoryEntry {
  stage: string;
  enteredAt?: string;
  exitedAt?: string;
}

interface ActionLogEntry {
  id: string;
  actionType: string;
  actionDescription: string;
  fromStage?: string;
  toStage?: string;
  performedByUserId?: string;
  performedByUserName?: string;
  metadata?: Record<string, any>;
  createdAt: string;
}

interface ApiStageHistoryEntry {
  stage: string;
  stageLabel: string;
  enteredAt?: string;
  exitedAt?: string;
  durationMs?: number;
  durationFormatted?: string;
  performedByUserId?: string;
  performedByUserName?: string;
  status: 'completed' | 'current' | 'pending' | 'skipped';
}

interface ActionLogsResponse {
  logs: ActionLogEntry[];
  stageHistory: ApiStageHistoryEntry[];
  totalCount: number;
  summary: {
    totalActions: number;
    firstAction?: string;
    lastAction?: string;
    currentStage?: string;
    timeInCurrentStage?: number;
  };
}

interface DeliveryAddressSnapshot {
  street: string;
  city: string;
  province: string;
  postal_code: string;
  delivery_instructions?: string;
}

interface MetadataBillingData {
  medications?: Array<{
    medicationName: string;
    strength: string;
    quantity: number;
    daysSupply: number;
    rxNumber: string;
    din?: string;
    upc?: string;
  }>;
  contactValue?: string;
  contactMethod?: string;
  convertedAt?: string;
}

interface PackagingScan {
  barcode: string;
  lotNumber: string;
  expiryDate: string;
  timestamp: string;
  confirmed?: boolean;
  quantity?: number; // GP-384: Quantity from this stock bottle (part-packaging)
}

interface PackagingItem {
  rxNumber?: string;
  din: string;
  lotNumber?: string;
  expiryDate?: string;
  scans?: PackagingScan[];
  quantityPackaged: number;
  packageType: string;
  labelPrintedAt?: string;
}

interface OrderMetadata {
  /** PRX-143: the PHOX Entry request(s) the order was entered from. */
  phoxEntrySources?: PhoxEntrySource[];
  billingData?: MetadataBillingData;
  clinicalVerification?: {
    verifiedBy?: string;
    verifiedAt?: string;
    notes?: string;
  };
  packagingData?: PackagingItem[] | {
    packagedBy?: string;
    packagedAt?: string;
    labelPrintedAt?: string;
  };
  productVerification?: {
    verifiedBy?: string;
    verifiedAt?: string;
  };
  descartesCreatedAt?: string;
  internalNotes?: string;
}

interface OrderDetailResponse {
  id: string;
  orderNumber?: string;
  rxNumber?: string;
  billingItemId?: string;
  status: string;
  priority?: string;
  workflowStage?: string;
  patientId?: string;
  patientName?: string;
  medicationName?: string;
  quantity?: number;
  daysSupply?: number;
  totalAmount?: string;
  insuranceAmount?: string;
  copayAmount?: string;
  paymentStatus?: string;
  paymentMethod?: string | null;
  paymentCollectedAt?: string | null;
  deliveryMethod?: string;
  deliveryAddressSnapshot?: DeliveryAddressSnapshot;
  deliveryDate?: string;
  deliveredAt?: string | null;
  readyForPickupAt?: string | null;
  pickedUpAt?: string | null;
  pickedUpBy?: string | null;
  descartesShipmentId?: string;
  descartesTrackingNumber?: string | null;
  descartesPlannedArrivalTime?: string | null;
  descartesOriginalPlannedArrivalTime?: string | null;
  descartesTrackId?: string | null;
  assignedTo?: string;
  assignedAt?: string;
  clinicalVerifiedAt?: string | null;
  clinicalVerifiedBy?: string | null;
  packagedAt?: string | null;
  packagedBy?: string | null;
  productVerifiedAt?: string | null;
  productVerifiedBy?: string | null;
  stagedAt?: string | null;
  stagedBy?: string | null;
  /** ShipStation label stamp — a courier order's Ready to Ship evidence (GP-1432). */
  shipstationShippedAt?: string | null;
  cancelledAt?: string | null;
  cancelledBy?: string | null;
  cancellationReason?: string | null;
  smsLastSentAt?: string | null;
  smsConfirmedAt?: string | null;
  reminderSentAt?: string | null;
  stagingSMSSentAt?: string | null;
  inTroubleshooting?: boolean;
  troubleshootingReason?: string;
  internalNote?: string;
  deliveryOrderNote?: string;
  metadata?: OrderMetadata;
  /** PRX-143 (WP-15): the server could not read the order's linked PHOX Entry requests. */
  phoxEntrySourcesUnavailable?: boolean;
  createdAt: string;
  updatedAt: string;
  stageHistory?: StageHistoryEntry[];
}

interface PipelineStageDefinition {
  id: string;
  label: string;
  synonyms: string[];
}

interface PipelineStage extends PipelineStageDefinition {
  status: 'completed' | 'current' | 'pending' | 'error';
  timestamp?: string;
  user?: string;
}

interface StageMeta {
  startedAt?: string;
  enteredAt?: string;
  completedAt?: string;
  assignedTo?: string;
  status?: string;
  expectedCompletion?: string;
}

const STAGE_DEFINITIONS: PipelineStageDefinition[] = [
  { id: 'phox-entry', label: 'Phox Entry', synonyms: ['phoxentry', 'phox', 'entry'] },
  { id: 'billing', label: 'Billing Queue', synonyms: ['billing', 'billingqueue', 'insurance'] },
  { id: 'schedule', label: 'Scheduling Queue', synonyms: ['schedule', 'scheduledelivery', 'deliveryschedule', 'scheduled'] },
  { id: 'clinical', label: 'Clinical Verification', synonyms: ['clinicalverification', 'clinical', 'verification', 'clinicallyverified'] },
  { id: 'packaging', label: 'Packaging', synonyms: ['packaging', 'packaged'] },
  { id: 'verify', label: 'Product Verification', synonyms: ['productverification', 'productverify', 'verified'] },
  { id: 'ready', label: 'Ready to Ship', synonyms: ['readytoship', 'ready', 'staged'] },
  { id: 'delivery', label: 'Completion', synonyms: ['delivery', 'outfordelivery', 'delivered', 'complete', 'picked_up', 'pickedup', 'pickup'] },
];

const STAGE_ICON_MAP: Record<string, React.ReactElement> = {
  'phox-entry': <Assignment />,
  billing: <Receipt />,
  schedule: <ScheduleIcon />,
  clinical: <Science />,
  packaging: <Inventory />,
  verify: <VerifiedUser />,
  ready: <LocalShipping />,
  delivery: <CheckCircle />,
};

const normalizeStageValue = (value?: string) =>
  (value || '').toLowerCase().replace(/[^a-z]/g, '');

const formatDateTime = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return format(date, 'MMM d, yyyy - h:mm a');
};

const formatDate = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return format(date, 'MMM d, yyyy');
};

/**
 * GP-1431: `deliveryDate` is a date-ONLY column ('2026-08-18'). `new Date()` on it
 * is UTC midnight, so `formatDateTime` rendered it as "Aug 17, 2026 - 8:00 PM" in
 * Eastern — a different day than the same field shows everywhere else, plus an
 * invented time. Split the calendar parts instead (repo date-handling rules).
 */
const formatDateOnly = (value?: string | null) => {
  if (!value) return null;
  const [year, month, day] = String(value).split('T')[0].split('-').map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) return null;
  return format(date, 'MMM d, yyyy');
};

const formatLabel = (value?: string | null) => {
  if (!value) return 'N/A';
  return value
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const derivePipelineState = (order: OrderDetailResponse | null) => {
  const defaultStages: PipelineStage[] = STAGE_DEFINITIONS.map((stage, index) => ({
    ...stage,
    status: index === 0 ? 'current' : 'pending',
  }));

  if (!order) {
    return { stages: defaultStages, currentStageIndex: 0, progress: 0, stageMeta: {} };
  }

  const stageMeta: Record<string, StageMeta> = {};
  const completed = new Set<string>();
  let currentStageIndex = 0;

  const stageIdMap: Record<string, string> = {
    'billing': 'billing',
    'scheduling': 'schedule',
    // GP-1338 (mirrored from OrderDetail's copy): `clinic_scheduling` is the real
    // WorkflowStage value — `scheduling` above never appears in the enum. Without
    // it a reopened order parked there mapped to nothing and fell back to index 0.
    'clinic_scheduling': 'schedule',
    'clinical_verification': 'clinical',
    'packaging': 'packaging',
    'product_verification': 'verify',
    'ready_to_ship': 'ready',
    'ready_for_pickup': 'ready',
    'staging': 'delivery',
    'out_for_delivery': 'delivery',
    'delivery_missed': 'delivery',
    'delivered': 'delivery',
    'picked_up': 'delivery',
  };

  stageMeta['phox-entry'] = { completedAt: order.createdAt, status: 'completed' };
  completed.add('phox-entry');

  if (order.metadata?.billingData?.convertedAt) {
    stageMeta['billing'] = { completedAt: order.metadata.billingData.convertedAt, status: 'completed' };
    completed.add('billing');
  }

  const descartesCreatedAt = order.metadata?.descartesCreatedAt;
  if (descartesCreatedAt || order.descartesShipmentId) {
    stageMeta['schedule'] = { completedAt: descartesCreatedAt || order.createdAt, status: 'completed' };
    completed.add('schedule');
  }

  const clinicalVerifiedAt = order.metadata?.clinicalVerification?.verifiedAt || order.clinicalVerifiedAt;
  const clinicalVerifiedBy = order.metadata?.clinicalVerification?.verifiedBy || order.clinicalVerifiedBy;
  if (clinicalVerifiedAt) {
    stageMeta['clinical'] = { completedAt: clinicalVerifiedAt, assignedTo: clinicalVerifiedBy || undefined, status: 'completed' };
    completed.add('clinical');
  }

  const packagingData = order.metadata?.packagingData;
  let packagedAt = order.metadata?.packagingData && !Array.isArray(order.metadata.packagingData)
    ? order.metadata.packagingData.packagedAt
    : order.packagedAt;
  let packagedBy = order.metadata?.packagingData && !Array.isArray(order.metadata.packagingData)
    ? order.metadata.packagingData.packagedBy
    : order.packagedBy;

  if (Array.isArray(packagingData) && !packagedAt) {
    const latestScan = packagingData
      .flatMap(p => p.scans || [])
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0];
    if (latestScan) packagedAt = latestScan.timestamp;
  }
  if (packagedAt) {
    stageMeta['packaging'] = { completedAt: packagedAt, assignedTo: packagedBy || undefined, status: 'completed' };
    completed.add('packaging');
  }

  const productVerifiedAt = order.metadata?.productVerification?.verifiedAt || order.productVerifiedAt;
  const productVerifiedBy = order.metadata?.productVerification?.verifiedBy || order.productVerifiedBy;
  if (productVerifiedAt) {
    stageMeta['verify'] = { completedAt: productVerifiedAt, assignedTo: productVerifiedBy || undefined, status: 'completed' };
    completed.add('verify');
  }

  // GP-1432: mirrors `derivePipelineState` in pages/orders/OrderDetail.tsx — this file
  // carries a second copy of that derivation, so fixes have to land in both until it is
  // extracted. A ShipStation courier has no Descartes shipment and `stagedAt` is a column
  // nothing writes, so the label stamp is its proof the parcel left the shelf.
  const readyToShipAt =
    order.stagedAt || order.metadata?.descartesCreatedAt || order.shipstationShippedAt || undefined;
  if (readyToShipAt) {
    const currentStage = order.workflowStage;
    const isComplete = currentStage && currentStage !== 'ready_to_ship' && currentStage !== 'staging' &&
      ['out_for_delivery', 'delivered'].includes(currentStage);
    stageMeta['ready'] = {
      enteredAt: readyToShipAt,
      completedAt: isComplete ? readyToShipAt : undefined,
      assignedTo: order.stagedBy || undefined,
      status: isComplete ? 'completed' : 'current',
    };
    if (isComplete) completed.add('ready');
  }

  const deliveredAt = order.deliveredAt || order.pickedUpAt;
  if (deliveredAt) {
    stageMeta['delivery'] = { completedAt: deliveredAt, status: 'completed' };
    completed.add('delivery');
  }

  if (order.workflowStage) {
    const mappedStageId = stageIdMap[order.workflowStage] || order.workflowStage;
    const stageIndex = STAGE_DEFINITIONS.findIndex((stage) => stage.id === mappedStageId);
    if (stageIndex >= 0) currentStageIndex = stageIndex;
  }

  const normalizedStatus = normalizeStageValue(order.status);
  const errorStatuses = ['cancelled', 'failed', 'problem'];
  const deliveredStatuses = ['delivered', 'completed'];

  // GP-1338 (mirrored from OrderDetail's copy): a terminal status only earns the
  // terminal painting while it is still TRUE — a reopened order keeps its stale
  // `completed` status but must render at its live stage, not all-green/100%.
  const { isReopenedAfterCompletion } = deriveOrderCompletionState(order);
  const isTerminallyComplete =
    !isReopenedAfterCompletion &&
    deliveredStatuses.some((token) => normalizedStatus.includes(token));

  if (isTerminallyComplete) {
    currentStageIndex = STAGE_DEFINITIONS.length - 1;
    STAGE_DEFINITIONS.forEach((stage) => completed.add(stage.id));
  }

  // The pipeline regresses when the order does — first-pass timestamps still
  // render underneath, but stages past the live one stop reading as done.
  if (isReopenedAfterCompletion) {
    STAGE_DEFINITIONS.slice(currentStageIndex).forEach((stage) => completed.delete(stage.id));
  }

  const isError = errorStatuses.some((token) => normalizedStatus.includes(token));

  const stages: PipelineStage[] = STAGE_DEFINITIONS.map((stage, index) => {
    let status: PipelineStage['status'] = 'pending';
    if (completed.has(stage.id)) status = 'completed';
    if (index === currentStageIndex) status = isError ? 'error' : 'current';
    if (isTerminallyComplete && index === STAGE_DEFINITIONS.length - 1) {
      status = 'completed';
    }
    return { ...stage, status, timestamp: stageMeta[stage.id]?.completedAt || stageMeta[stage.id]?.startedAt, user: stageMeta[stage.id]?.assignedTo };
  });

  // GP-1432 (mirror of OrderDetail.tsx): count the nodes that are FINISHED, not the
  // index of the node the order sits on — the old formula read 100% the moment an
  // order reached the last node, over a pipeline that was still in progress.
  const completedStageCount = stages.filter((stage) => stage.status === 'completed').length;
  const computedProgress = (completedStageCount / Math.max(STAGE_DEFINITIONS.length, 1)) * 100;
  const progress = isTerminallyComplete
    ? 100
    : Math.min(100, Math.max(0, Math.round(computedProgress)));

  return { stages, currentStageIndex, progress, stageMeta };
};

const StyledConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: { top: 22 },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: { backgroundImage: 'linear-gradient(95deg, #ffc107 0%, #ff9800 50%, #f44336 100%)' },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: { backgroundImage: 'linear-gradient(95deg, #4caf50 0%, #66bb6a 50%, #81c784 100%)' },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3, border: 0, backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#eaeaf0', borderRadius: 1,
  },
}));

const StepIconRoot = styled('div')<{ ownerState: { completed?: boolean; active?: boolean; error?: boolean } }>(({ theme, ownerState }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#ccc',
  zIndex: 1, color: '#fff', width: 48, height: 48, display: 'flex', borderRadius: '50%', justifyContent: 'center', alignItems: 'center',
  ...(ownerState.active && { backgroundImage: 'linear-gradient(136deg, #ffc107 0%, #ff9800 50%, #f44336 100%)', boxShadow: '0 4px 10px 0 rgba(0,0,0,.25)' }),
  ...(ownerState.completed && { backgroundImage: 'linear-gradient(136deg, #4caf50 0%, #66bb6a 50%, #81c784 100%)' }),
  ...(ownerState.error && { backgroundColor: theme.palette.error.main }),
}));

const StepIconComponent = (props: any) => {
  const { active, completed, error, icon } = props;
  const icons: Record<string, React.ReactElement> = {
    1: <Assignment />, 2: <Receipt />, 3: <ScheduleIcon />, 4: <Science />,
    5: <Inventory />, 6: <VerifiedUser />, 7: <LocalShipping />, 8: <CheckCircle />,
  };
  return (
    <StepIconRoot ownerState={{ completed, active, error }}>
      {completed ? <CheckCircle /> : icons[String(icon)]}
    </StepIconRoot>
  );
};

const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  const normalized = status?.toLowerCase();
  const map: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
    pending: 'warning', confirmed: 'info', processing: 'primary', in_progress: 'warning',
    clinically_verified: 'primary', packaged: 'primary', verified: 'primary', ready_to_ship: 'success',
    staged: 'success', out_for_delivery: 'info', delivered: 'success', completed: 'success',
    cancelled: 'error', failed: 'error', on_hold: 'warning', problem: 'error',
  };
  return map[normalized] || 'default';
};

const getPriorityColor = (priority?: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  const normalized = priority?.toLowerCase();
  switch (normalized) {
    case 'critical': case 'urgent': return 'error';
    case 'high': return 'warning';
    case 'low': return 'info';
    default: return 'default';
  }
};

const JSONViewer: React.FC<{ data: any }> = ({ data }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Box sx={{ position: 'relative', bgcolor: 'grey.100', p: 2, borderRadius: 1, overflow: 'auto', maxHeight: 400 }}>
      <Tooltip title={copied ? "Copied!" : "Copy JSON"}>
        <IconButton size="small" onClick={handleCopy} sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'white' }}>
          {copied ? <CheckCircle color="success" fontSize="small" /> : <ContentCopy fontSize="small" />}
        </IconButton>
      </Tooltip>
      <pre style={{ margin: 0, fontSize: '0.75rem', fontFamily: 'monospace' }}>{JSON.stringify(data, null, 2)}</pre>
    </Box>
  );
};

const EmbeddedOrderDetailView: React.FC<EmbeddedOrderDetailViewProps> = ({ orderId, pharmacyId, onBack }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [order, setOrder] = useState<OrderDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [signatures, setSignatures] = useState<HardcopySignature[]>([]);
  const [signaturesLoading, setSignaturesLoading] = useState(false);
  const [activeHardcopyTab, setActiveHardcopyTab] = useState(0);
  const [actionLogs, setActionLogs] = useState<ActionLogsResponse | null>(null);
  const [actionLogsLoading, setActionLogsLoading] = useState(false);
  const [openVerificationPhoto, setOpenVerificationPhoto] = useState<VerificationPhoto | null>(null);
  // PRX-68: every product-verification photo on this order — passed to every
  // hardcopy tab, which filters to its own Rx (plus any pack-scope photo).
  const { data: verificationPhotosData } = useVerificationPhotos(pharmacyId, orderId);
  const verificationPhotos = Array.isArray(verificationPhotosData) ? verificationPhotosData : [];

  const pipelineState = useMemo(() => derivePipelineState(order), [order]);
  const { stages: pipelineStages, currentStageIndex, progress: pipelineProgress, stageMeta } = pipelineState;
  const currentStage = pipelineStages[currentStageIndex] ?? pipelineStages[pipelineStages.length - 1];
  const currentStageMeta = currentStage ? stageMeta[currentStage.id] : undefined;

  useEffect(() => {
    if (pharmacyId && orderId) {
      fetchOrderDetails();
    } else {
      setLoading(false);
    }
  }, [pharmacyId, orderId]);

  const fetchOrderDetails = async (useRefreshSpinner = false) => {
    if (!pharmacyId || !orderId) return;

    try {
      if (useRefreshSpinner) setRefreshing(true);
      else setLoading(true);
      setError(null);
      const data = await orderService.getOrderById(pharmacyId, orderId);
      setOrder(data);

      // GP-1395: billing snapshot UNION line items — same rule as OrderDetail.
      const medications = deriveOrderDrugList(data);
      if (medications.length > 0) {
        const rxNumbers = medications.map((med) => med.rxNumber).filter((rxNum) => rxNum);
        if (rxNumbers.length > 0) fetchSignatures(rxNumbers);
      } else if (data.rxNumber) {
        fetchSignatures(data.rxNumber);
      }
    } catch (err: any) {
      console.error('Error fetching order details:', err);
      setError(err?.message || 'Failed to load order details');
    } finally {
      if (useRefreshSpinner) setRefreshing(false);
      else setLoading(false);
    }
  };

  const fetchSignatures = async (rxNumber: string | string[]) => {
    if (!pharmacyId || !rxNumber) return;

    try {
      setSignaturesLoading(true);
      if (Array.isArray(rxNumber)) {
        const response = await api.get(`/api/rx/${pharmacyId}/signatures-batch?rxNumbers=${encodeURIComponent(rxNumber.join(','))}`);
        if (response.data.success) setSignatures(response.data.data.signatures || []);
      } else {
        const response = await api.get(`/api/rx/${pharmacyId}/signatures/${rxNumber}`);
        if (response.data.success) setSignatures(response.data.data.signatures || []);
      }
    } catch (err: any) {
      console.error('Error fetching signatures:', err);
      setSignatures([]);
    } finally {
      setSignaturesLoading(false);
    }
  };

  const fetchActionLogs = async () => {
    if (!pharmacyId || !orderId) return;

    try {
      setActionLogsLoading(true);
      const data = await orderService.getOrderActionLogs(pharmacyId, orderId);
      setActionLogs(data);
    } catch (err: any) {
      console.error('Error fetching action logs:', err);
      setActionLogs(null);
    } finally {
      setActionLogsLoading(false);
    }
  };

  useEffect(() => {
    if (order && pharmacyId && orderId) fetchActionLogs();
  }, [order?.id]);

  const handleRefresh = () => fetchOrderDetails(true);

  const orderIdentifier = order?.orderNumber || order?.rxNumber || order?.id || orderId;
  const patientName = order?.patientName || 'Unknown Patient';
  const patientCompositeId = order?.patientId && pharmacyId
    ? (order.patientId.includes('_') ? order.patientId : `${pharmacyId}_${order.patientId}`)
    : null;
  // PRX-85: top bar recent-patients trigger — this view is embedded (History
  // tab of the unified patient dialog), so it mounts/unmounts with the
  // selected order rather than needing an `open` gate of its own.
  useCurrentPatientContext(
    patientCompositeId && order?.patientName && order.patientName !== 'Unknown Patient'
      ? { compositeId: patientCompositeId, pharmacyId, displayName: order.patientName }
      : undefined,
  );
  const primaryMedication = order?.medicationName ? {
    name: order.medicationName,
    strength: order.metadata?.billingData?.medications?.[0]?.strength || '',
  } : null;
  // GP-1431: one authoritative date, named for what it is — the same value the
  // Order History Delivery column and the delivery queues show. Was labelled
  // "Ship Date" and rendered through the timestamp formatter (see formatDateOnly).
  const isPickupOrder =
    !!order?.deliveryMethod?.toLowerCase().includes('pickup') ||
    !!order?.workflowStage?.toLowerCase().includes('pickup');
  const scheduledDateLabel =
    formatDateOnly(order?.deliveryDate) ||
    formatDateOnly(currentStageMeta?.expectedCompletion) ||
    'Not scheduled';
  const updatedAtLabel = formatDateTime(order?.updatedAt);
  const progressValue = Math.max(0, Math.min(100, Math.round(pipelineProgress)));
  const statusLabel = formatLabel(order?.status);
  const priorityLabel = formatLabel(order?.priority);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={onBack} sx={{ mb: 2 }}>Back to History</Button>
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => fetchOrderDetails()}>Retry</Button>}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!order) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={onBack} sx={{ mb: 2 }}>Back to History</Button>
        <Alert severity="warning">Order not found.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', overflow: 'auto' }}>
      <Stack spacing={2}>
        {/* Header with back button */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button startIcon={<ArrowBack />} onClick={onBack} variant="outlined" size="small">
            Back to History
          </Button>
          <Typography variant="h6">Order #{orderIdentifier}</Typography>
          <Chip label={statusLabel} color={getStatusColor(order.status)} size="small" />
          {/* PRX-143: entered from a PHOX Entry request — link back to its thread */}
          <PhoxEntrySourceChips
            metadata={order.metadata}
            unavailable={order.phoxEntrySourcesUnavailable}
            pharmacyId={pharmacyId}
          />
          <Box sx={{ flexGrow: 1 }} />
          <IconButton onClick={handleRefresh} disabled={refreshing} size="small">
            <Refresh sx={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
          </IconButton>
        </Box>

        {/* Cancelled Alert */}
        {order?.status === 'cancelled' && (
          <Alert severity="error" icon={<ErrorIcon />}>
            <Typography variant="subtitle2" gutterBottom>This order has been cancelled</Typography>
            {order.cancelledAt && <Typography variant="body2">Cancelled on: {formatDateTime(order.cancelledAt)}</Typography>}
            {order.cancelledBy && <Typography variant="body2">Cancelled by: {order.cancelledBy}</Typography>}
            {order.cancellationReason && <Typography variant="body2">Reason: {order.cancellationReason}</Typography>}
          </Alert>
        )}

        {/* Order Summary */}
        <Paper sx={{ p: 2 }}>
          <Stack direction="row" spacing={2} flexWrap="wrap" rowGap={1} alignItems="center">
            <Typography variant="body2"><strong>Patient:</strong> {patientName}</Typography>
            {primaryMedication && (
              <Typography variant="body2"><strong>Drug:</strong> {primaryMedication.name} {primaryMedication.strength ? `- ${primaryMedication.strength}` : ''}</Typography>
            )}
            <Typography variant="body2"><strong>Updated:</strong> {updatedAtLabel || 'Unknown'}</Typography>
            <Chip label={priorityLabel === 'N/A' ? 'Standard' : priorityLabel} color={getPriorityColor(order.priority)} size="small" variant="outlined" />
          </Stack>
          <Box mt={2}>
            <LinearProgress variant="determinate" value={progressValue} sx={{ height: 6, borderRadius: 3 }} />
            <Typography variant="caption" color="text.secondary">{progressValue}% Complete • {isPickupOrder ? 'Pickup Date' : 'Delivery Date'}: {scheduledDateLabel}</Typography>
          </Box>
        </Paper>

        {/* Pipeline */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Order Progress</Typography>
          <Stepper alternativeLabel activeStep={currentStageIndex} connector={<StyledConnector />} sx={{ mt: 1 }}>
            {pipelineStages.map((stage) => (
              <Step key={stage.id} completed={stage.status === 'completed'}>
                <StepLabel StepIconComponent={StepIconComponent} error={stage.status === 'error'}>
                  <Typography variant="caption" fontWeight={stage.status === 'current' ? 'bold' : 'normal'}>{stage.label}</Typography>
                  {stage.timestamp && <Typography variant="caption" display="block" color="text.secondary">{formatDate(stage.timestamp)}</Typography>}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* Timeline and Hardcopy */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>Order Timeline</Typography>
            <OrderTimelineView pharmacyId={pharmacyId} orderId={orderId} billingItemId={order?.billingItemId} />
          </Grid>
          {(() => {
            // GP-1395: a drug present on the order must always get a hardcopy
            // tab, even when the billing snapshot has drifted (see orderDrugList).
            const medications = deriveOrderDrugList(order);
            const hasMultipleMedications = medications.length > 1;
            const hasMedications = medications.length > 0;
            const hasRxNumber = order.rxNumber;
            if (!hasMedications && !hasRxNumber) return null;
            return (
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>{hasMultipleMedications ? 'Prescription Hardcopies' : 'Prescription Hardcopy'}</Typography>
                {signaturesLoading ? (
                  <Box display="flex" justifyContent="center" py={3}><CircularProgress size={24} /></Box>
                ) : (
                  <Box sx={{ maxHeight: 600, overflowY: 'auto' }}>
                    {hasMultipleMedications ? (
                      <>
                        <Tabs value={activeHardcopyTab} onChange={(e, v) => setActiveHardcopyTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 1 }}>
                          {medications.map((med: any, i: number) => <Tab key={i} label={`Rx ${med.rxNumber || i + 1}`} />)}
                        </Tabs>
                        {medications.map((med: any, i: number) => (
                          <Box key={i} hidden={activeHardcopyTab !== i}>
                            {activeHardcopyTab === i && (
                              <HardcopyViewer rxNumber={med.rxNumber} pharmacyId={pharmacyId} signatures={signatures.filter(sig => String(sig.rxNumber) === String(med.rxNumber))} editable={false} packagingData={Array.isArray(order.metadata?.packagingData) ? order.metadata?.packagingData : undefined} verificationPhotos={verificationPhotos} onOpenVerificationPhoto={(photo) => setOpenVerificationPhoto(photo)} />
                            )}
                          </Box>
                        ))}
                      </>
                    ) : (
                      <HardcopyViewer rxNumber={(hasMedications ? medications[0].rxNumber : order.rxNumber) || ''} pharmacyId={pharmacyId} signatures={signatures} editable={false} packagingData={Array.isArray(order.metadata?.packagingData) ? order.metadata?.packagingData : undefined} verificationPhotos={verificationPhotos} onOpenVerificationPhoto={(photo) => setOpenVerificationPhoto(photo)} />
                    )}
                  </Box>
                )}
              </Grid>
            );
          })()}
        </Grid>

        {/* Patient & Delivery Info */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <Person color="primary" fontSize="small" />
                  <Typography variant="subtitle2">Patient Information</Typography>
                </Stack>
                <Typography variant="body2"><strong>Name:</strong> {patientName}</Typography>
                {order.patientId && <Typography variant="body2"><strong>ID:</strong> {order.patientId}</Typography>}
                {order.metadata?.billingData?.contactValue && <Typography variant="body2"><strong>Phone:</strong> {order.metadata.billingData.contactValue}</Typography>}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <LocalShipping color="primary" fontSize="small" />
                  <Typography variant="subtitle2">Delivery Information</Typography>
                </Stack>
                <Typography variant="body2"><strong>Method:</strong> {formatLabel(order.deliveryMethod) || 'Not specified'}</Typography>
                <Typography variant="body2"><strong>Date:</strong> {formatDate(order.deliveryDate) || 'Not scheduled'}</Typography>
                {order.deliveryAddressSnapshot && (
                  <Typography variant="body2"><strong>Address:</strong> {order.deliveryAddressSnapshot.street}, {order.deliveryAddressSnapshot.city}</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Medications */}
        <Card variant="outlined">
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <LocalPharmacy color="primary" fontSize="small" />
              <Typography variant="subtitle2">Medications</Typography>
            </Stack>
            {order.metadata?.billingData?.medications && order.metadata.billingData.medications.length > 0 ? (
              <Stack spacing={1}>
                {order.metadata.billingData.medications.map((med, i) => {
                  // Same zero-fill detection as OrderDetail medications card.
                  const fillInfo = (order.metadata as any)?.partialFillInfo?.filledQuantities?.find(
                    (f: any) => f.rxNumber === med.rxNumber,
                  );
                  const isFullOwing = !!fillInfo && Number(fillInfo.quantityPackaged) === 0;
                  return (
                    <Paper
                      key={i}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        ...(isFullOwing && {
                          borderColor: 'warning.main',
                          borderWidth: 2,
                          bgcolor: 'rgba(237, 108, 2, 0.04)',
                        }),
                      }}
                    >
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                            <Typography variant="body2"><strong>{med.medicationName}</strong></Typography>
                            {isFullOwing && (
                              <Chip
                                label="Full Balance Owing"
                                size="small"
                                color="warning"
                                sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600 }}
                              />
                            )}
                          </Stack>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="caption" color="text.secondary">
                            Qty: {isFullOwing ? `0 of ${fillInfo.quantityPrescribed}` : med.quantity}
                          </Typography>
                        </Grid>
                        <Grid item xs={3}><Typography variant="caption" color="text.secondary">Rx: {med.rxNumber}</Typography></Grid>
                      </Grid>
                    </Paper>
                  );
                })}
              </Stack>
            ) : order.medicationName ? (
              <Typography variant="body2">{order.medicationName} - Qty: {order.quantity || 'N/A'}</Typography>
            ) : (
              <Typography color="text.secondary">No medications recorded</Typography>
            )}
          </CardContent>
        </Card>

        {/* Billing & SMS */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <Payment color="primary" fontSize="small" />
                  <Typography variant="subtitle2">Billing</Typography>
                </Stack>
                <Typography variant="body2"><strong>Total:</strong> ${order.totalAmount ? Number(order.totalAmount).toFixed(2) : '0.00'}</Typography>
                <Typography variant="body2"><strong>Copay:</strong> ${order.copayAmount ? Number(order.copayAmount).toFixed(2) : '0.00'}</Typography>
                <Chip size="small" label={formatLabel(order.paymentStatus)} color={getStatusColor(order.paymentStatus || '')} variant="outlined" sx={{ mt: 0.5 }} />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <Sms color="primary" fontSize="small" />
                  <Typography variant="subtitle2">SMS Activity</Typography>
                </Stack>
                <Stack spacing={0.5}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <NotificationsActive fontSize="small" color={order.smsLastSentAt ? 'success' : 'disabled'} />
                    <Typography variant="caption">{order.smsLastSentAt ? `Sent: ${formatDateTime(order.smsLastSentAt)}` : 'Not sent'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle fontSize="small" color={order.smsConfirmedAt ? 'success' : 'disabled'} />
                    <Typography variant="caption">{order.smsConfirmedAt ? `Confirmed: ${formatDateTime(order.smsConfirmedAt)}` : 'Awaiting'}</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Notes */}
        {(order.internalNote || order.metadata?.clinicalVerification?.notes || order.troubleshootingReason) && (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>Notes</Typography>
              {order.internalNote && <Typography variant="body2" sx={{ mb: 1 }}><strong>Internal:</strong> {order.internalNote}</Typography>}
              {order.metadata?.clinicalVerification?.notes && <Typography variant="body2" sx={{ mb: 1 }}><strong>Clinical:</strong> {order.metadata.clinicalVerification.notes}</Typography>}
              {order.troubleshootingReason && <Alert severity="warning" sx={{ mt: 1 }}>{order.troubleshootingReason}</Alert>}
            </CardContent>
          </Card>
        )}

        {/* Action Log */}
        <Card variant="outlined">
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <History color="primary" fontSize="small" />
              <Typography variant="subtitle2">Action Log</Typography>
              {actionLogs && <Chip label={`${actionLogs.totalCount} actions`} size="small" variant="outlined" />}
            </Stack>
            {actionLogsLoading ? (
              <Box display="flex" justifyContent="center" py={2}><CircularProgress size={20} /></Box>
            ) : actionLogs?.logs && actionLogs.logs.length > 0 ? (
              <TableContainer sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Time</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Action</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>User</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {actionLogs.logs.slice(0, 10).map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell><Typography variant="caption">{formatDateTime(log.createdAt)}</Typography></TableCell>
                        <TableCell><Typography variant="body2">{formatTimelineDescription(log.actionDescription)}</Typography></TableCell>
                        <TableCell><UserName userName={log.performedByUserName} userId={log.performedByUserId} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">No action logs</Typography>
            )}
          </CardContent>
        </Card>

        {/* Raw JSON */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Code fontSize="small" />
              <Typography variant="subtitle2">Raw Data</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <JSONViewer data={order} />
          </AccordionDetails>
        </Accordion>
      </Stack>

      {/* Product verification photo lightbox (PRX-68) */}
      <VerificationPhotoLightbox
        open={openVerificationPhoto !== null}
        onClose={() => setOpenVerificationPhoto(null)}
        pharmacyId={pharmacyId}
        orderId={orderId}
        photo={openVerificationPhoto}
        orderNumber={order.orderNumber}
      />
    </Box>
  );
};

export default EmbeddedOrderDetailView;
